(() => {
  const containerSelector = '.shopify_subscriptions_app_container';
  const blockSelector = '.shopify_subscriptions_app_block';
  const oneTimeSelector = 'input[type="radio"][data-radio-type="one_time_purchase"]';
  const planDropdownSelector = '.group_dropdown, .shopify_subscriptions_group_dropdown';
  const preferences = new WeakMap();

  const defaultPreference = {
    radioType: 'one_time_purchase',
    sellingPlanId: '',
  };

  const getPreference = (container) => {
    if (!preferences.has(container)) {
      preferences.set(container, { ...defaultPreference });
    }

    return preferences.get(container);
  };

  const updateSellingPlanInputs = (container, sellingPlanId = '') => {
    const section = container.closest('[id^="shopify-section-"]') || document;

    section
      .querySelectorAll('input.selected-selling-plan-id[name="selling_plan"], form[action*="/cart/add"] input[name="selling_plan"]')
      .forEach((input) => {
        input.value = sellingPlanId;
      });
  };

  const getCurrentVariantId = (container) => {
    const section = container.closest('[id^="shopify-section-"]') || document;
    const productId = container.dataset.productId;
    const addToCartForms = Array.from(section.querySelectorAll('form[action*="/cart/add"]')).filter((form) => {
      const productIdInput = form.elements['product-id'];
      return !productIdInput || productIdInput.value === productId;
    });

    return addToCartForms
      .map((form) => form.querySelector('input[name="id"], select[name="id"]')?.value)
      .find(Boolean);
  };

  const getCurrentSellingPlanBlock = (container) => {
    const variantId = getCurrentVariantId(container);

    return variantId ? container.querySelector(`${blockSelector}[data-variant-id="${variantId}"]`) : null;
  };

  const syncGroupRadioWithDropdown = (groupRadio, dropdown) => {
    const selectedOption = dropdown?.options?.[dropdown.selectedIndex];
    if (!selectedOption) return;

    groupRadio.dataset.sellingPlanId = dropdown.value;
    groupRadio.dataset.variantPrice = selectedOption.dataset.variantPrice;
    groupRadio.dataset.variantCompareAtPrice = selectedOption.dataset.variantCompareAtPrice;
    groupRadio.dataset.sellingPlanAdjustment = selectedOption.dataset.sellingPlanAdjustment;
  };

  const checkRadio = (radio, dispatchChange = false) => {
    if (!radio?.name) return;

    radio
      .closest(blockSelector)
      ?.querySelectorAll('input[type="radio"]')
      .forEach((siblingRadio) => {
        if (siblingRadio.name === radio.name && siblingRadio !== radio) {
          siblingRadio.checked = false;
        }
      });

    radio.checked = true;

    if (dispatchChange) {
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const findSellingPlanRadio = (sellingPlanBlock, sellingPlanId) => {
    if (sellingPlanId) {
      const matchingRadio = sellingPlanBlock.querySelector(
        `input[type="radio"][data-selling-plan-id="${sellingPlanId}"]`
      );
      if (matchingRadio) return matchingRadio;

      const matchingDropdown = Array.from(sellingPlanBlock.querySelectorAll(planDropdownSelector)).find((dropdown) =>
        Array.from(dropdown.options).some((option) => option.value === sellingPlanId)
      );
      if (matchingDropdown) {
        matchingDropdown.value = sellingPlanId;

        const groupRadio = sellingPlanBlock.querySelector(
          `input[type="radio"][data-selling-plan-group-id="${matchingDropdown.dataset.sellingPlanGroupId}"]`
        );
        if (groupRadio) {
          syncGroupRadioWithDropdown(groupRadio, matchingDropdown);
          return groupRadio;
        }
      }
    }

    return sellingPlanBlock.querySelector(
      'input[type="radio"][data-radio-type="selling_plan_group"], input[type="radio"][data-radio-type="selling_plan"]'
    );
  };

  const applyPreference = (container, dispatchCurrentChange = false) => {
    const preference = getPreference(container);
    const currentBlock = getCurrentSellingPlanBlock(container);
    let currentSellingPlanId = '';

    container.querySelectorAll(blockSelector).forEach((sellingPlanBlock) => {
      const shouldDispatch = dispatchCurrentChange && sellingPlanBlock === currentBlock;

      if (preference.radioType === 'one_time_purchase') {
        const oneTimeInput = sellingPlanBlock.querySelector(oneTimeSelector);
        if (!oneTimeInput || oneTimeInput.disabled) return;

        checkRadio(oneTimeInput, shouldDispatch);
        return;
      }

      const sellingPlanRadio = findSellingPlanRadio(sellingPlanBlock, preference.sellingPlanId);
      if (!sellingPlanRadio || sellingPlanRadio.disabled) return;

      checkRadio(sellingPlanRadio, shouldDispatch);

      if (sellingPlanBlock === currentBlock) {
        currentSellingPlanId = sellingPlanRadio.dataset.sellingPlanId || preference.sellingPlanId || '';
      }
    });

    updateSellingPlanInputs(container, currentSellingPlanId);

    return true;
  };

  const applyPreferences = (dispatchCurrentChange = false) => {
    document
      .querySelectorAll(containerSelector)
      .forEach((container) => applyPreference(container, dispatchCurrentChange));
  };

  const setPreferenceFromControl = (control) => {
    const container = control.closest(containerSelector);
    if (!container) return;

    if (control.matches(planDropdownSelector)) {
      preferences.set(container, {
        radioType: 'selling_plan_group',
        sellingPlanId: control.value || '',
      });
      applyPreference(container, false);
      return;
    }

    if (!control.matches('input[type="radio"][data-radio-type]')) return;

    if (control.dataset.radioType === 'one_time_purchase') {
      preferences.set(container, { ...defaultPreference });
      applyPreference(container, false);
      return;
    }

    preferences.set(container, {
      radioType: control.dataset.radioType,
      sellingPlanId: control.dataset.sellingPlanId || '',
    });
    applyPreference(container, false);
  };

  const scheduleDefault = () => {
    requestAnimationFrame(() => {
      applyPreferences(true);
      setTimeout(() => applyPreferences(true), 150);
      setTimeout(() => applyPreferences(true), 500);
    });
  };

  const scheduleAfterVariantChange = () => {
    applyPreferences(false);
    setTimeout(() => applyPreferences(true), 100);
    setTimeout(() => applyPreferences(true), 350);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleDefault, { once: true });
  } else {
    scheduleDefault();
  }

  document.addEventListener('shopify:section:load', scheduleDefault);
  document.addEventListener('change', (event) => {
    if (event.target.closest(containerSelector)) {
      setPreferenceFromControl(event.target);
      return;
    }

    if (event.target.closest('variant-selects')) {
      scheduleAfterVariantChange();
    }
  }, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, scheduleAfterVariantChange);
  }
})();
